import React from 'react';
import FlashCard from '@/components/ui/FlashCard';
import { cn } from '@/lib/utils';
import { AnimationState } from '@/hooks/useFlashCardManager';
import { ANIMATION_TIMINGS } from '@/constants/animations';

/**
 * Props for the FlashCardAnimation component
 */
interface FlashCardAnimationProps {
  // Content props
  frontContent: string;         // Content to display on the front of the card
  backContent: string;          // Content to display on the back of the card
  currentExerciseId: string;    // Unique ID for the current exercise (used for keying)
  
  // Interaction props
  isFlipped: boolean;           // Whether the card is currently flipped
  onFlip: () => void;           // Callback when the card is flipped
  

  
  // Animation state
  /**
   * Current animation state of the card from the useFlashCardManager hook
   * This drives all animations and transitions for the card
   */
  animationState: AnimationState; // Required animation state from useFlashCardManager
  

}

/**
 * Component that handles the display of the active flashcard with smooth animations
 * 
 * This is a focused component that:
 * 1. Renders a single FlashCard with the provided content
 * 2. Handles the flip animation through the FlashCard component
 * 3. Passes through swipe handlers for parent components to manage
 * 4. Applies different animations based on the current animationState
 * 
 * The animation sequence is driven by the enum AnimationState from useFlashCardManager:
 * - AnimationState.IDLE: Card is at rest, ready for interaction
 * - AnimationState.FLIPPING: 3D rotation animation is occurring
 * - AnimationState.EXCELLENT: Positive feedback animation is showing
 * - AnimationState.EXITING: Card is leaving the view
 * - AnimationState.RETURNING: Card is returning to the stack
 * 
 * All animation timing is coordinated through the ANIMATION_TIMINGS constants
 */
const FlashCardAnimation: React.FC<FlashCardAnimationProps> = ({
  frontContent,
  backContent,
  currentExerciseId,
  isFlipped,
  onFlip,
  animationState
}) => {
  // This handler ensures that flip events are properly propagated to parent
  const handleFlip = () => {
    onFlip();
  };

  // Determine additional animation classes based on animation state
  const getAnimationClasses = () => {
    switch (animationState) {
      case AnimationState.FLIPPING:
        return "z-[60]"; // Ensure flipping card is above others
      case AnimationState.EXCELLENT:
        return "z-[60]"; // Keep on top during the excellent animation
      case AnimationState.RETURNING:
        return "animate-bounce-once"; // Add a subtle bounce for returning cards
      case AnimationState.EXITING:
        // Use a consistent exit animation without directional swiping
        return "fade-out";
      case AnimationState.IDLE:
      default:
        return ""; // No special animation in idle state
    }
  };

  return (
    <div className="relative w-full h-full">
      <div className={cn(
        "w-full h-full rounded-xl overflow-hidden card-perspective",
        "transition-all",
        `duration-${ANIMATION_TIMINGS.FLIP_DURATION}`,
        // Add additional classes based on animation state
        getAnimationClasses()
      )}>
        <FlashCard
          key={currentExerciseId}
          front={frontContent}
          back={backContent}
          onFlip={handleFlip}
          isFlipped={isFlipped}
          className="card-shadow-md transform-none"
          // Force front side on initial render for consistent appearance
          forceInitialFront={true}
        />
      </div>
    </div>
  );
};

export default FlashCardAnimation;
